import React, { useState, useEffect, FunctionComponent, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaUser, FaUtensils, FaBullseye, FaQuestionCircle, FaHeadset, FaTimes, FaBars, FaCrown, FaCamera, FaChevronRight, FaCheck, FaApple, FaGooglePlay, FaChartLine, FaUsers, FaFire, FaWeight, FaHeart } from 'react-icons/fa';
import { fetchUserProfile, updateUserProfile, fetchApplicationSettings, ApplicationSettings } from '../lib/api';
import { fetchLogs, fetchMacroGoals } from '../lib/tracker-api';

const FaChartLineIcon = FaChartLine as FunctionComponent<React.SVGProps<SVGSVGElement>>;
const FaUsersIcon = FaUsers as FunctionComponent<React.SVGProps<SVGSVGElement>>;
const FaFireIcon = FaFire as FunctionComponent<React.SVGProps<SVGSVGElement>>;
const FaWeightIcon = FaWeight as FunctionComponent<React.SVGProps<SVGSVGElement>>;
const FaHeartIcon = FaHeart as FunctionComponent<React.SVGProps<SVGSVGElement>>;

// Define icon components with proper type casting
const FaUserIcon = FaUser as FunctionComponent<React.SVGProps<SVGSVGElement>>;
const FaUtensilsIcon = FaUtensils as FunctionComponent<React.SVGProps<SVGSVGElement>>;
const FaBullseyeIcon = FaBullseye as FunctionComponent<React.SVGProps<SVGSVGElement>>;
const FaQuestionCircleIcon = FaQuestionCircle as FunctionComponent<React.SVGProps<SVGSVGElement>>;
const FaHeadsetIcon = FaHeadset as FunctionComponent<React.SVGProps<SVGSVGElement>>;
const FaTimesIcon = FaTimes as FunctionComponent<React.SVGProps<SVGSVGElement>>;
const FaBarsIcon = FaBars as FunctionComponent<React.SVGProps<SVGSVGElement>>;
const FaCrownIcon = FaCrown as FunctionComponent<React.SVGProps<SVGSVGElement>>;
const FaCameraIcon = FaCamera as FunctionComponent<React.SVGProps<SVGSVGElement>>;
const FaChevronRightIcon = FaChevronRight as FunctionComponent<React.SVGProps<SVGSVGElement>>;
const FaCheckIcon = FaCheck as FunctionComponent<React.SVGProps<SVGSVGElement>>;
const FaAppleIcon = FaApple as FunctionComponent<React.SVGProps<SVGSVGElement>>;
const FaGooglePlayIcon = FaGooglePlay as FunctionComponent<React.SVGProps<SVGSVGElement>>;

interface Props {
  setIsLoggedIn: (value: boolean) => void;
}

interface Bmi {
  id: number;
  documentId: string;
  currentWeight: number;
  idealBmiWeight: number;
  height: number;
  gender: string;
  dateOfBirth: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
}

interface ProfilePicture {
  id: number;
  documentId: string;
  name: string;
  alternativeText?: string;
  caption?: string;
  width: number;
  height: number;
  formats?: {
    large?: { url: string };
    small?: { url: string };
    medium?: { url: string };
    thumbnail?: { url: string };
  };
  hash: string;
  ext: string;
  mime: string;
  size: number;
  url: string;
  previewUrl?: string;
  provider: string;
  providerMetadata?: any;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
}

interface Ingredient {
  id: number;
  documentId: string;
  ingredientName: string;
  slug?: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
}

interface User {
  id: number;
  documentId: string;
  username: string;
  email: string;
  provider: string;
  confirmed: boolean;
  blocked: boolean;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  dietPreference?: string;
  stripeCustomerId?: string;
  userInterests?: Ingredient[];
  goalWeight?: number;
  goalReminder?: string;
  personalGoalNote?: string;
  bmi?: Bmi;
  profilePicture?: ProfilePicture;
}

const Profile: React.FC<Props> = () => {
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ [key: string]: boolean }>({});
  const [form, setForm] = useState({
    username: '',
    email: '',
    dietPreference: 'Carnivore',
    userInterests: [] as Ingredient[],
    goalWeight: 0,
    goalReminder: 'daily',
    personalGoalNote: '',
    currentWeight: 0,
    height: 0,
    gender: 'Male',
    dateOfBirth: '',
  });
  const [availableIngredients] = useState<Ingredient[]>([
    { id: 1, documentId: '1', ingredientName: 'Eggs', createdAt: '', updatedAt: '', publishedAt: '' },
    { id: 2, documentId: '2', ingredientName: 'Honey', createdAt: '', updatedAt: '', publishedAt: '' },
    { id: 3, documentId: '3', ingredientName: 'Cheese', createdAt: '', updatedAt: '', publishedAt: '' },
    { id: 4, documentId: '4', ingredientName: 'Yogurt', createdAt: '', updatedAt: '', publishedAt: '' },
    { id: 5, documentId: '5', ingredientName: 'Butter', createdAt: '', updatedAt: '', publishedAt: '' },
    { id: 6, documentId: '6', ingredientName: 'Milk', createdAt: '', updatedAt: '', publishedAt: '' },
  ]);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('Information');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [profilePictureFile, setProfilePictureFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [appSettings, setAppSettings] = useState<ApplicationSettings | null>(null);
  const [todayLogs, setTodayLogs] = useState<any[]>([]);
  const [macroGoals, setMacroGoals] = useState<any>(null);

  useEffect(() => {
    loadProfile();
    loadAppSettings();
    loadTodayData();
  }, []);

  const loadTodayData = async () => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      const [logs, goals] = await Promise.all([
        fetchLogs(today, today).catch(() => []),
        fetchMacroGoals().catch(() => null),
      ]);
      setTodayLogs(logs || []);
      setMacroGoals(goals);
    } catch {}
  };

  const loadAppSettings = async () => {
    try {
      const settings = await fetchApplicationSettings();
      setAppSettings(settings);
    } catch (err) {
      console.error('Failed to load app settings');
    }
  };

  const handleProfilePictureChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setProfilePictureFile(file);
      const reader = new FileReader();
      reader.onload = () => setPreviewUrl(reader.result as string);
      reader.readAsDataURL(file);
    }
  }, []);

  const uploadProfilePicture = useCallback(async () => {
    if (!profilePictureFile) return;

    setUploadingImage(true);
    try {
      const token = localStorage.getItem('jwt');
      const userId = localStorage.getItem('userId');
      if (!token || !userId) throw new Error('Not authenticated');

      const formData = new FormData();
      formData.append('files', profilePictureFile);
      formData.append('refId', userId);
      formData.append('ref', 'plugin::users-permissions.user');
      formData.append('field', 'profile_picture');

      const response = await fetch(`${process.env.REACT_APP_STRAPI_API_URL || 'https://www.eatmeatapp.com'}/api/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to upload image');

      setSuccessMessage('Profile picture updated successfully!');
      setProfilePictureFile(null);
      setPreviewUrl(null);
      await loadProfile();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to upload profile picture');
    } finally {
      setUploadingImage(false);
    }
  }, [profilePictureFile]);

  const handlePasswordChange = useCallback(async () => {
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setError('All password fields are required');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('jwt');
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(`${process.env.REACT_APP_STRAPI_API_URL || 'https://www.eatmeatapp.com'}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          password: passwordForm.newPassword,
          passwordConfirmation: passwordForm.confirmPassword,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to change password');
      }

      setSuccessMessage('Password changed successfully!');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordFields(false);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  }, [passwordForm]);

  const calculateBMI = useCallback((weight: number, height: number): number => {
    if (!weight || !height) return 0;
    const heightInMeters = height / 100;
    return Math.round((weight / (heightInMeters * heightInMeters)) * 10) / 10;
  }, []);

  const getBMICategory = useCallback((bmi: number): { category: string; color: string } => {
    if (bmi < 18.5) return { category: 'Underweight', color: 'text-blue-600' };
    if (bmi < 25) return { category: 'Normal', color: 'text-green-600' };
    if (bmi < 30) return { category: 'Overweight', color: 'text-yellow-600' };
    return { category: 'Obese', color: 'text-red-600' };
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('jwt');
      if (!token) throw new Error('No token found');
      const response = await fetchUserProfile(token);
      const userData = response.data ? response : response;
      const baseUrl = process.env.REACT_APP_STRAPI_API_URL || 'http://localhost:1444';
      const user: User = {
        id: userData.id,
        documentId: userData.documentId || '',
        username: userData.username || '',
        email: userData.email || '',
        provider: userData.provider || '',
        confirmed: userData.confirmed || false,
        blocked: userData.blocked || false,
        createdAt: userData.createdAt || '',
        updatedAt: userData.updatedAt || '',
        publishedAt: userData.publishedAt || '',
        dietPreference: userData.diet_preference,
        stripeCustomerId: userData.stripeCustomerId,
        userInterests: Array.isArray(userData.ingredients)
          ? userData.ingredients.map((i: any) => ({
            id: i.id,
            documentId: i.documentId || '',
            ingredientName: i.ingredient_name || '',
            slug: i.slug,
            createdAt: i.createdAt || '',
            updatedAt: i.updatedAt || '',
            publishedAt: i.publishedAt || '',
          }))
          : [],
        goalWeight: userData.goal_weight,
        goalReminder: userData.goal_reminder,
        personalGoalNote: userData.personal_goal_note,
        bmi: userData.bmi
          ? {
            id: userData.bmi.id,
            documentId: userData.bmi.documentId || '',
            currentWeight: userData.bmi.currentWeight || 0,
            idealBmiWeight: userData.bmi.idealBmiWeight ? parseFloat(userData.bmi.idealBmiWeight) : 0,
            height: userData.bmi.height || 0,
            gender: userData.bmi.Gender || '',
            dateOfBirth: userData.bmi.date_of_birth || '',
            createdAt: userData.bmi.createdAt || '',
            updatedAt: userData.bmi.updatedAt || '',
            publishedAt: userData.bmi.publishedAt || '',
          }
          : undefined,
        profilePicture: userData.profile_picture
          ? {
            id: userData.profile_picture.id,
            documentId: userData.profile_picture.documentId || '',
            name: userData.profile_picture.name || '',
            alternativeText: userData.profile_picture.alternativeText,
            caption: userData.profile_picture.caption,
            width: userData.profile_picture.width || 0,
            height: userData.profile_picture.height || 0,
            formats: userData.profile_picture.formats,
            hash: userData.profile_picture.hash || '',
            ext: userData.profile_picture.ext || '',
            mime: userData.profile_picture.mime || '',
            size: userData.profile_picture.size || 0,
            url: userData.profile_picture.url ? `${baseUrl}${userData.profile_picture.url}` : '',
            previewUrl: userData.profile_picture.previewUrl,
            provider: userData.profile_picture.provider || '',
            providerMetadata: userData.profile_picture.provider_metadata,
            createdAt: userData.profile_picture.createdAt || '',
            updatedAt: userData.profile_picture.updatedAt || '',
            publishedAt: userData.profile_picture.publishedAt || '',
          }
          : undefined,
      };
      setUser(user);
      setForm({
        username: user.username || '',
        email: user.email || '',
        dietPreference: user.dietPreference || 'Carnivore',
        userInterests: user.userInterests || [],
        goalWeight: user.goalWeight || 0,
        goalReminder: user.goalReminder || 'daily',
        personalGoalNote: user.personalGoalNote || '',
        currentWeight: user.bmi?.currentWeight || 0,
        height: user.bmi?.height || 0,
        gender: user.bmi?.gender || 'Male',
        dateOfBirth: user.bmi?.dateOfBirth || '',
      });
    } catch (err) {
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const calculateIdealBMI = (height: number, gender: string): number => {
    const heightInMeters = height / 100;
    const bmi = gender === 'Male' ? 22.5 : 21.5;
    return Math.round(bmi * (heightInMeters * heightInMeters) * 10) / 10;
  };

  const handleEdit = (tab: string) => {
    setEditing((prev) => ({ ...prev, [tab]: true }));
  };

  const handleCancel = (tab: string) => {
    setEditing((prev) => ({ ...prev, [tab]: false }));
    setForm((prev) => ({
      ...prev,
      ...(tab === 'Information' && {
        username: user?.username || '',
        email: user?.email || '',
        currentWeight: user?.bmi?.currentWeight || 0,
        height: user?.bmi?.height || 0,
        gender: user?.bmi?.gender || 'Male',
        dateOfBirth: user?.bmi?.dateOfBirth || '',
      }),
      ...(tab === 'Diet' && { dietPreference: user?.dietPreference || 'Carnivore' }),
      ...(tab === 'Interests' && { userInterests: user?.userInterests || [] }),
      ...(tab === 'Goals' && {
        goalWeight: user?.goalWeight || 0,
        goalReminder: user?.goalReminder || 'daily',
        personalGoalNote: user?.personalGoalNote || '',
      }),
    }));
  };

  const handleSubmit = async (tab: string, e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!validateForm(tab)) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('jwt');
      const userId = localStorage.getItem('userId');
      if (!token || !userId) throw new Error('Not authenticated');
      let updateData: any = {};

      if (tab === 'Information') {
        const idealBmiWeight = calculateIdealBMI(form.height, form.gender);
        updateData = {
          username: form.username,
          email: form.email,
          bmi: {
            currentWeight: form.currentWeight,
            height: form.height,
            Gender: form.gender,
            date_of_birth: form.dateOfBirth,
            idealBmiWeight,
          },
        };
      } else if (tab === 'Diet') {
        updateData = { diet_preference: form.dietPreference };
      } else if (tab === 'Interests') {
        updateData = { ingredients: form.userInterests.map((i) => i.id) };
      } else if (tab === 'Goals') {
        updateData = {
          goal_weight: form.goalWeight,
          goal_reminder: form.goalReminder,
          personal_goal_note: form.personalGoalNote,
        };
      }

      await updateUserProfile(parseInt(userId), updateData, token);
      setEditing((prev) => ({ ...prev, [tab]: false }));
      setSuccessMessage('Profile updated successfully!');
      await loadProfile();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (tab: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (tab === 'Information') {
      if (!form.username.trim()) {
        setError('Username is required');
        return false;
      }
      if (form.username.trim().length < 3) {
        setError('Username must be at least 3 characters');
        return false;
      }
      if (!emailRegex.test(form.email)) {
        setError('Invalid email format');
        return false;
      }
      if (form.height <= 0 || form.currentWeight <= 0) {
        setError('Height and weight must be positive');
        return false;
      }
      if (form.height < 100 || form.height > 250) {
        setError('Height must be between 100-250 cm');
        return false;
      }
      if (form.currentWeight < 30 || form.currentWeight > 300) {
        setError('Weight must be between 30-300 kg');
        return false;
      }
    }
    if (tab === 'Goals' && form.goalWeight > 0) {
      if (form.goalWeight < 30 || form.goalWeight > 300) {
        setError('Goal weight must be between 30-300 kg');
        return false;
      }
    }
    setError(null);
    return true;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === 'goalWeight' || name === 'currentWeight' || name === 'height' ? parseFloat(value) || 0 : value,
    }));
  };

  const handleInterestChange = (ingredient: Ingredient) => {
    setForm((prev) => {
      const userInterests = prev.userInterests.some((i) => i.id === ingredient.id)
        ? prev.userInterests.filter((i) => i.id !== ingredient.id)
        : [...prev.userInterests, ingredient];
      return { ...prev, userInterests };
    });
  };

  const toggleMobileMenu = () => setMobileMenuOpen((prev) => !prev);

  const handleTabClick = (tabName: string) => {
    setActiveTab(tabName);
    setMobileMenuOpen(false);
  };

  const tabs = [
    { name: 'Information', icon: FaUserIcon },
    { name: 'Diet', icon: FaUtensilsIcon },
    { name: 'Goals', icon: FaBullseyeIcon },
    { name: 'Subscription', icon: FaCrownIcon },
    { name: 'Support', icon: FaHeadsetIcon },
  ];

  if (loading && !user) {
    return (
      <div className="profile-loading">
        <div className="loading-spinner" aria-label="Loading" />
      </div>
    );
  }

  // Dashboard metrics
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const todayMacros = todayLogs.filter((l: any) => l.log_type === 'Macro Log');
  const todayProtein = todayMacros.reduce((s: number, l: any) => s + (l.protein || 0), 0);
  const todayFat = todayMacros.reduce((s: number, l: any) => s + (l.fat || 0), 0);
  const todayCalories = todayMacros.reduce((s: number, l: any) => s + (l.calories || 0), 0);
  const proteinPct = macroGoals?.daily_protein_goal ? Math.min((todayProtein / macroGoals.daily_protein_goal) * 100, 100) : 0;
  const fatPct = macroGoals?.daily_fat_goal ? Math.min((todayFat / macroGoals.daily_fat_goal) * 100, 100) : 0;
  const caloriePct = macroGoals?.daily_calorie_goal ? Math.min((todayCalories / macroGoals.daily_calorie_goal) * 100, 100) : 0;
  const todayMood = todayLogs.find((l: any) => l.log_type === 'Mood Log');
  const todayWeight = todayLogs.find((l: any) => l.log_type === 'Weight Track');
  const bmiValue = user?.bmi?.height && user?.bmi?.currentWeight ? calculateBMI(user.bmi.currentWeight, user.bmi.height) : 0;
  const goalDiff = user?.goalWeight && user?.bmi?.currentWeight ? user.goalWeight - user.bmi.currentWeight : null;

  const Ring = ({ pct, color, size = 64, stroke = 6, bgColor = '#f3f4f6', children }: { pct: number; color: string; size?: number; stroke?: number; bgColor?: string; children?: React.ReactNode }) => {
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const offset = c - (Math.min(pct, 100) / 100) * c;
    return (
      <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={r} stroke={bgColor} strokeWidth={stroke} fill="none" />
          <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth={stroke} fill="none" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={offset} style={{ transition: 'stroke-dashoffset 0.5s' }} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">{children}</div>
      </div>
    );
  };

  return (
    <div className="profile-container">
      {/* ═══ Dashboard Hero ═══ */}
      {user && (
        <>
          {/* Page header strip — avatar, greeting, CTA only */}
          <div className="bg-white border-b border-gray-100">
            <div className="max-w-6xl mx-auto px-4 md:px-6 py-4 md:py-5">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <button onClick={toggleMobileMenu} className="md:hidden w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center text-gray-600" aria-label="Toggle menu">
                    {mobileMenuOpen ? <FaTimesIcon /> : <FaBarsIcon />}
                  </button>
                  <div className="w-11 h-11 md:w-12 md:h-12 rounded-2xl overflow-hidden bg-gradient-to-br from-primary/10 to-orange-100 border border-gray-100 flex-shrink-0">
                    {user.profilePicture ? (
                      <img src={user.profilePicture.url} alt={user.username} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-primary">
                        <FaUserIcon style={{ width: 20, height: 20 }} />
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="text-[11px] md:text-xs text-gray-500 font-medium">{greeting},</div>
                    <h1 className="text-lg md:text-xl font-extrabold tracking-tight leading-tight text-gray-900">{user.username} 👋</h1>
                  </div>
                </div>
                <Link to="/tracker" className="inline-flex items-center gap-2 bg-primary text-white font-semibold px-4 py-2 rounded-full text-sm hover:shadow-lg hover:-translate-y-0.5 transition-all shadow-md shadow-primary/20">
                  <FaChartLineIcon style={{ width: 12, height: 12 }} /> Log Today
                </Link>
              </div>
            </div>
          </div>

          {/* Stats & quick actions on neutral background */}
          <div className="bg-gray-50 border-b border-gray-100">
            <div className="max-w-6xl mx-auto px-4 md:px-6 py-5 md:py-6 space-y-4">
              {/* Stats strip — white cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3 shadow-sm">
                  <Ring pct={caloriePct} color="#f97316" size={52} stroke={5}>
                    <FaFireIcon style={{ width: 16, height: 16, color: '#f97316' }} />
                  </Ring>
                  <div className="min-w-0">
                    <div className="text-xs text-gray-400 font-medium">Today</div>
                    <div className="text-lg font-bold text-gray-800 leading-tight">{todayCalories.toFixed(0)}</div>
                    <div className="text-[10px] text-gray-400">{macroGoals?.daily_calorie_goal ? `/ ${macroGoals.daily_calorie_goal} kcal` : 'kcal'}</div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3 shadow-sm">
                  <Ring pct={proteinPct} color="#ef4444" size={52} stroke={5}>
                    <span className="text-base">🥩</span>
                  </Ring>
                  <div className="min-w-0">
                    <div className="text-xs text-gray-400 font-medium">Protein</div>
                    <div className="text-lg font-bold text-gray-800 leading-tight">{todayProtein.toFixed(0)}g</div>
                    <div className="text-[10px] text-gray-400">{macroGoals?.daily_protein_goal ? `of ${macroGoals.daily_protein_goal}g` : 'no goal'}</div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3 shadow-sm">
                  <div className="w-13 h-13 rounded-full bg-blue-50 flex items-center justify-center" style={{ width: 52, height: 52 }}>
                    <FaWeightIcon style={{ width: 18, height: 18, color: '#3b82f6' }} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs text-gray-400 font-medium">Current</div>
                    <div className="text-lg font-bold text-gray-800 leading-tight">{todayWeight?.weight_kg?.toFixed(1) ?? user.bmi?.currentWeight ?? '—'}<span className="text-sm font-normal text-gray-500">kg</span></div>
                    <div className="text-[10px] text-gray-400">{bmiValue > 0 ? `BMI ${bmiValue}` : 'Set in profile'}</div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3 shadow-sm">
                  <div className="rounded-full bg-green-50 flex items-center justify-center" style={{ width: 52, height: 52 }}>
                    <span className="text-xl">🎯</span>
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs text-gray-400 font-medium">Goal</div>
                    <div className="text-lg font-bold text-gray-800 leading-tight">{user.goalWeight ? `${user.goalWeight}kg` : '—'}</div>
                    <div className="text-[10px] text-gray-400">
                      {goalDiff !== null ? `${goalDiff > 0 ? '+' : ''}${goalDiff.toFixed(1)} kg to ${goalDiff < 0 ? 'lose' : 'gain'}` : 'Set a goal'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick action cards + diet badge + mood pill */}
              <div className="flex flex-wrap items-center gap-2">
                {user.dietPreference && (
                  <div className="inline-flex items-center gap-1.5 bg-white border border-gray-100 rounded-full px-3 py-1.5 text-xs font-semibold text-gray-700 shadow-sm">
                    <FaUtensilsIcon style={{ width: 10, height: 10, color: '#f97316' }} /> {user.dietPreference}
                  </div>
                )}
                {todayMood && (
                  <div className="inline-flex items-center gap-1.5 bg-white border border-gray-100 rounded-full px-3 py-1.5 text-xs text-gray-700 shadow-sm">
                    <FaHeartIcon style={{ width: 10, height: 10, color: '#ef4444' }} />
                    <strong className="text-gray-900">{todayMood.mood_rating}</strong>
                    <span className="text-gray-400">· Energy {todayMood.energy_level}/10</span>
                  </div>
                )}
                <Link to="/tracker" className="inline-flex items-center gap-1.5 bg-white border border-gray-100 rounded-full px-3 py-1.5 text-xs font-semibold text-gray-700 hover:border-primary hover:text-primary transition-colors shadow-sm">
                  <span>📋</span> {todayLogs.length} entries today
                </Link>
                <Link to="/community" className="inline-flex items-center gap-1.5 bg-white border border-gray-100 rounded-full px-3 py-1.5 text-xs font-semibold text-gray-700 hover:border-primary hover:text-primary transition-colors shadow-sm">
                  <FaUsersIcon style={{ width: 10, height: 10 }} /> Community
                </Link>
                <button onClick={() => setActiveTab('Goals')} className="inline-flex items-center gap-1.5 bg-white border border-gray-100 rounded-full px-3 py-1.5 text-xs font-semibold text-gray-700 hover:border-primary hover:text-primary transition-colors shadow-sm">
                  <span>🎯</span> Edit Goals
                </button>
                <button onClick={() => setActiveTab('Subscription')} className="inline-flex items-center gap-1.5 bg-white border border-gray-100 rounded-full px-3 py-1.5 text-xs font-semibold text-gray-700 hover:border-primary hover:text-primary transition-colors shadow-sm">
                  <FaCrownIcon style={{ width: 10, height: 10, color: '#f59e0b' }} /> Premium
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Legacy header — hidden when dashboard hero is shown */}
      {!user && (
        <div className="profile-header">
          <div className="profile-header-content">
            <button onClick={toggleMobileMenu} className="profile-menu-btn md:hidden" aria-label="Toggle menu">
              {mobileMenuOpen ? <FaTimesIcon /> : <FaBarsIcon />}
            </button>
            <h1 className="profile-title">My Profile</h1>
          </div>
        </div>
      )}

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="profile-overlay" onClick={() => setMobileMenuOpen(false)}>
          <div className="profile-mobile-sidebar" onClick={(e) => e.stopPropagation()}>
            <div className="profile-mobile-sidebar-header">
              <h2>Menu</h2>
              <button onClick={() => setMobileMenuOpen(false)} aria-label="Close menu">
                <FaTimesIcon />
              </button>
            </div>
            <nav className="profile-nav">
              {tabs.map((tab) => (
                <button
                  key={tab.name}
                  className={`profile-nav-item ${activeTab === tab.name ? 'active' : ''}`}
                  onClick={() => handleTabClick(tab.name)}
                >
                  <tab.icon className="profile-nav-icon" />
                  <span>{tab.name}</span>
                  <FaChevronRightIcon className="profile-nav-arrow" />
                </button>
              ))}
            </nav>
          </div>
        </div>
      )}

      {/* Main Layout */}
      <div className="profile-layout">
        {/* Desktop Sidebar */}
        <aside className="profile-sidebar">
          <nav className="profile-nav">
            {tabs.map((tab) => (
              <button
                key={tab.name}
                className={`profile-nav-item ${activeTab === tab.name ? 'active' : ''}`}
                onClick={() => handleTabClick(tab.name)}
              >
                <tab.icon className="profile-nav-icon" />
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
          <div style={{ borderTop: '1px solid #e5e7eb', margin: '12px 0', paddingTop: '12px' }}>
            <p style={{ fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px', paddingLeft: '12px' }}>Quick Links</p>
            <Link to="/tracker" className="profile-nav-item" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', color: '#374151', textDecoration: 'none', borderRadius: '8px' }}>
              <FaChartLineIcon className="profile-nav-icon" />
              <span>Health Tracker</span>
            </Link>
            <Link to="/community" className="profile-nav-item" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', color: '#374151', textDecoration: 'none', borderRadius: '8px' }}>
              <FaUsersIcon className="profile-nav-icon" />
              <span>Community</span>
            </Link>
          </div>
        </aside>

        {/* Content Area */}
        <main className="profile-main">
          {/* Alerts */}
          {error && (
            <div className="profile-alert error">
              <span>{error}</span>
              <button onClick={() => setError(null)}><FaTimesIcon /></button>
            </div>
          )}
          {successMessage && (
            <div className="profile-alert success">
              <span>{successMessage}</span>
              <button onClick={() => setSuccessMessage(null)}><FaTimesIcon /></button>
            </div>
          )}

          {user && (
            <>
              {/* Information Tab */}
              {activeTab === 'Information' && (
                <div className="profile-section">
                  <div className="profile-section-header">
                    <h2>Personal Information</h2>
                    {!editing['Information'] && (
                      <button className="profile-edit-btn" onClick={() => handleEdit('Information')}>
                        Edit
                      </button>
                    )}
                  </div>

                  {/* Profile Picture */}
                  <div className="profile-avatar-section">
                    <div className="profile-avatar">
                      {previewUrl ? (
                        <img src={previewUrl} alt="Preview" />
                      ) : user.profilePicture ? (
                        <img src={user.profilePicture.url} alt={user.profilePicture.alternativeText || 'Profile'} />
                      ) : (
                        <FaUserIcon className="profile-avatar-placeholder" />
                      )}
                      {!editing['Information'] && (
                        <label className="profile-avatar-upload">
                          <FaCameraIcon />
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleProfilePictureChange}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>
                    {profilePictureFile && (
                      <div className="profile-avatar-actions">
                        <button onClick={uploadProfilePicture} disabled={uploadingImage} className="btn-primary">
                          {uploadingImage ? 'Uploading...' : 'Save Photo'}
                        </button>
                        <button onClick={() => { setProfilePictureFile(null); setPreviewUrl(null); }} className="btn-secondary">
                          Cancel
                        </button>
                      </div>
                    )}
                    <h3 className="profile-name">{user.username}</h3>
                    <p className="profile-email">{user.email}</p>
                  </div>

                  {editing['Information'] ? (
                    <form onSubmit={(e) => handleSubmit('Information', e)} className="profile-form">
                      <div className="form-grid">
                        <div className="form-group">
                          <label>Username</label>
                          <input
                            type="text"
                            name="username"
                            value={form.username}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Email</label>
                          <input
                            type="email"
                            name="email"
                            value={form.email}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Date of Birth</label>
                          <input
                            type="date"
                            name="dateOfBirth"
                            value={form.dateOfBirth}
                            onChange={handleInputChange}
                          />
                        </div>
                        <div className="form-group">
                          <label>Gender</label>
                          <select name="gender" value={form.gender} onChange={handleInputChange}>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Height (cm)</label>
                          <input
                            type="number"
                            name="height"
                            value={form.height}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Weight (kg)</label>
                          <input
                            type="number"
                            name="currentWeight"
                            value={form.currentWeight}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>
                      <div className="form-actions">
                        <button type="submit" className="btn-primary">Save Changes</button>
                        <button type="button" onClick={() => handleCancel('Information')} className="btn-secondary">
                          Cancel
                        </button>
                      </div>
                    </form>
                  ) : (
                    <>
                      <div className="profile-info-grid">
                        <div className="profile-info-card">
                          <span className="profile-info-label">Date of Birth</span>
                          <span className="profile-info-value">
                            {user.bmi?.dateOfBirth ? new Date(user.bmi.dateOfBirth).toLocaleDateString() : 'Not set'}
                          </span>
                        </div>
                        <div className="profile-info-card">
                          <span className="profile-info-label">Gender</span>
                          <span className="profile-info-value">{user.bmi?.gender || 'Not set'}</span>
                        </div>
                        <div className="profile-info-card">
                          <span className="profile-info-label">Height</span>
                          <span className="profile-info-value">{user.bmi?.height ? `${user.bmi.height} cm` : 'Not set'}</span>
                        </div>
                        <div className="profile-info-card">
                          <span className="profile-info-label">Weight</span>
                          <span className="profile-info-value">{user.bmi?.currentWeight ? `${user.bmi.currentWeight} kg` : 'Not set'}</span>
                        </div>
                      </div>

                      {user.bmi?.height && user.bmi?.currentWeight && (
                        <div className="profile-bmi-card">
                          <div className="bmi-value">
                            <span className="bmi-number">{calculateBMI(user.bmi.currentWeight, user.bmi.height)}</span>
                            <span className="bmi-label">BMI</span>
                          </div>
                          <div className={`bmi-category ${getBMICategory(calculateBMI(user.bmi.currentWeight, user.bmi.height)).color}`}>
                            {getBMICategory(calculateBMI(user.bmi.currentWeight, user.bmi.height)).category}
                          </div>
                          <div className="bmi-ideal">
                            <span>Ideal weight: {user.bmi?.idealBmiWeight || calculateIdealBMI(user.bmi.height, user.bmi.gender)} kg</span>
                          </div>
                        </div>
                      )}

                      <div className="profile-security">
                        <button onClick={() => setShowPasswordFields(!showPasswordFields)} className="btn-outline">
                          Change Password
                        </button>
                      </div>

                      {showPasswordFields && (
                        <div className="password-form">
                          <div className="form-group">
                            <label>Current Password</label>
                            <input
                              type="password"
                              value={passwordForm.currentPassword}
                              onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                            />
                          </div>
                          <div className="form-group">
                            <label>New Password</label>
                            <input
                              type="password"
                              value={passwordForm.newPassword}
                              onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                            />
                          </div>
                          <div className="form-group">
                            <label>Confirm New Password</label>
                            <input
                              type="password"
                              value={passwordForm.confirmPassword}
                              onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                            />
                          </div>
                          <div className="form-actions">
                            <button onClick={handlePasswordChange} disabled={loading} className="btn-primary">
                              {loading ? 'Updating...' : 'Update Password'}
                            </button>
                            <button onClick={() => { setShowPasswordFields(false); setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' }); }} className="btn-secondary">
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Danger Zone */}
                      <div style={{ marginTop: '24px', padding: '16px', border: '1px solid #fecaca', background: '#fef2f2', borderRadius: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                          <span style={{ fontSize: '16px' }}>⚠️</span>
                          <span style={{ fontSize: '12px', fontWeight: 700, color: '#991b1b', textTransform: 'uppercase', letterSpacing: '1px' }}>Danger Zone</span>
                        </div>
                        <p style={{ fontSize: '13px', color: '#7f1d1d', marginBottom: '12px', lineHeight: 1.5 }}>
                          Permanently delete your account and all associated data. This cannot be undone.
                        </p>
                        <Link
                          to="/delete-profile"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '8px 16px',
                            background: 'white',
                            color: '#dc2626',
                            border: '1px solid #fca5a5',
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: 600,
                            textDecoration: 'none',
                            transition: 'all 0.15s ease',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#dc2626'; e.currentTarget.style.color = 'white'; e.currentTarget.style.borderColor = '#dc2626'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.borderColor = '#fca5a5'; }}
                        >
                          🗑 Delete Account
                        </Link>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Diet Tab */}
              {activeTab === 'Diet' && (
                <div className="profile-section">
                  <div className="profile-section-header">
                    <h2>Diet Preference</h2>
                    {!editing['Diet'] && (
                      <button className="profile-edit-btn" onClick={() => handleEdit('Diet')}>Edit</button>
                    )}
                  </div>

                  {editing['Diet'] ? (
                    <form onSubmit={(e) => handleSubmit('Diet', e)} className="profile-form">
                      <div className="diet-options">
                        {['Carnivore', 'Ketovore'].map((diet) => (
                          <label key={diet} className={`diet-option ${form.dietPreference === diet ? 'selected' : ''}`}>
                            <input
                              type="radio"
                              name="dietPreference"
                              value={diet}
                              checked={form.dietPreference === diet}
                              onChange={handleInputChange}
                            />
                            <div className="diet-option-content">
                              <span className="diet-option-name">{diet}</span>
                              <span className="diet-option-desc">
                                {diet === 'Carnivore' ? 'Animal-based foods only' : 'Carnivore + low-carb plants'}
                              </span>
                            </div>
                            {form.dietPreference === diet && <FaCheckIcon className="diet-option-check" />}
                          </label>
                        ))}
                      </div>
                      <div className="form-actions">
                        <button type="submit" className="btn-primary">Save</button>
                        <button type="button" onClick={() => handleCancel('Diet')} className="btn-secondary">Cancel</button>
                      </div>
                    </form>
                  ) : (
                    <div className="diet-current">
                      <div className="diet-badge">{user.dietPreference || 'Not set'}</div>
                      <p className="diet-description">
                        {user.dietPreference === 'Carnivore' && 'A diet focused on animal-based foods, excluding most plant-based items.'}
                        {user.dietPreference === 'Ketovore' && 'A hybrid diet combining carnivore and ketogenic principles.'}
                        {!user.dietPreference && 'Select a diet preference to get personalized recommendations.'}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Goals Tab */}
              {activeTab === 'Goals' && (
                <div className="profile-section">
                  <div className="profile-section-header">
                    <h2>Health Goals</h2>
                    {!editing['Goals'] && (
                      <button className="profile-edit-btn" onClick={() => handleEdit('Goals')}>Edit</button>
                    )}
                  </div>

                  {editing['Goals'] ? (
                    <form onSubmit={(e) => handleSubmit('Goals', e)} className="profile-form">
                      <div className="form-group">
                        <label>Goal Weight (kg)</label>
                        <input
                          type="number"
                          name="goalWeight"
                          value={form.goalWeight}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="form-group">
                        <label>Personal Note</label>
                        <textarea
                          name="personalGoalNote"
                          value={form.personalGoalNote}
                          onChange={handleInputChange}
                          placeholder="What motivates you?"
                        />
                      </div>
                      <div className="form-group">
                        <label>Reminder Frequency</label>
                        <select name="goalReminder" value={form.goalReminder} onChange={handleInputChange}>
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                        </select>
                      </div>
                      <div className="form-actions">
                        <button type="submit" className="btn-primary">Save</button>
                        <button type="button" onClick={() => handleCancel('Goals')} className="btn-secondary">Cancel</button>
                      </div>
                    </form>
                  ) : (
                    <div className="goals-display">
                      <div className="goals-progress">
                        <div className="goal-stat">
                          <span className="goal-stat-value">{user.bmi?.currentWeight || '—'}</span>
                          <span className="goal-stat-label">Current (kg)</span>
                        </div>
                        <div className="goal-arrow">→</div>
                        <div className="goal-stat">
                          <span className="goal-stat-value">{user.goalWeight || '—'}</span>
                          <span className="goal-stat-label">Goal (kg)</span>
                        </div>
                      </div>
                      {user.goalWeight && user.bmi?.currentWeight && (
                        <div className="goal-diff">
                          <span className={user.goalWeight < user.bmi.currentWeight ? 'text-orange-500' : 'text-green-500'}>
                            {user.goalWeight < user.bmi.currentWeight ? '' : '+'}
                            {(user.goalWeight - user.bmi.currentWeight).toFixed(1)} kg
                          </span>
                          <span> to {user.goalWeight < user.bmi.currentWeight ? 'lose' : 'gain'}</span>
                        </div>
                      )}
                      {user.personalGoalNote && (
                        <div className="goal-note">
                          <strong>Your motivation:</strong> {user.personalGoalNote}
                        </div>
                      )}
                      <div className="goal-reminder">
                        <strong>Reminders:</strong> {user.goalReminder || 'Not set'}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Subscription Tab */}
              {activeTab === 'Subscription' && (
                <div className="profile-section">
                  <div className="profile-section-header">
                    <h2>Subscription</h2>
                  </div>

                  <div className="subscription-info">
                    <div className="subscription-badge free">
                      <FaCrownIcon />
                      <span>Free Plan</span>
                    </div>
                    <p className="subscription-desc">
                      Upgrade to Premium in the EatMeat app to unlock all features including personalized meal plans,
                      advanced tracking, and exclusive carnivore recipes.
                    </p>

                    <div className="subscription-features">
                      <h4>Premium Features:</h4>
                      <ul>
                        <li><FaCheckIcon /> 500+ Premium Carnivore Recipes</li>
                        <li><FaCheckIcon /> Personalized Meal Plans</li>
                        <li><FaCheckIcon /> Advanced Progress Tracking</li>
                        <li><FaCheckIcon /> Priority Support</li>
                        <li><FaCheckIcon /> Ad-Free Experience</li>
                      </ul>
                    </div>

                    <div className="subscription-cta">
                      <p>Download the app to manage your subscription:</p>
                      <div className="app-download-buttons">
                        <a
                          href={appSettings?.iosAppLink || 'https://apps.apple.com/app/id6746769046'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="app-download-btn ios"
                        >
                          <FaAppleIcon />
                          <div>
                            <span className="small">Download on the</span>
                            <span className="large">App Store</span>
                          </div>
                        </a>
                        <a
                          href={appSettings?.androidAppLink || 'https://play.google.com/store/apps/details?id=com.carivore.lifestyle.eatmeat'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="app-download-btn android"
                        >
                          <FaGooglePlayIcon />
                          <div>
                            <span className="small">Get it on</span>
                            <span className="large">Google Play</span>
                          </div>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Support Tab */}
              {activeTab === 'Support' && (
                <div className="profile-section">
                  <div className="profile-section-header">
                    <h2>Support</h2>
                  </div>

                  <div className="support-content">
                    <div className="support-card">
                      <FaHeadsetIcon className="support-icon" />
                      <h3>Need Help?</h3>
                      <p>Our support team is here to assist you with any questions or issues.</p>
                      <button
                        className="btn-primary"
                        onClick={() => {
                          if (window.Tawk_API && window.Tawk_API.maximize) {
                            window.Tawk_API.maximize();
                          }
                        }}
                      >
                        Start Live Chat
                      </button>
                    </div>

                    <div className="support-links">
                      <a href="/faqs" className="support-link">
                        <FaQuestionCircleIcon />
                        <span>FAQs</span>
                        <FaChevronRightIcon />
                      </a>
                      <a href="/contact" className="support-link">
                        <FaHeadsetIcon />
                        <span>Contact Us</span>
                        <FaChevronRightIcon />
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <nav className="profile-bottom-nav">
        {tabs.map((tab) => (
          <button
            key={tab.name}
            className={`profile-bottom-nav-item ${activeTab === tab.name ? 'active' : ''}`}
            onClick={() => handleTabClick(tab.name)}
          >
            <tab.icon />
            <span>{tab.name}</span>
          </button>
        ))}
      </nav>
    </div>
  );
};

export default Profile;
